import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ColumnPinningState, ColumnOrderState } from '@tanstack/react-table'
import { TableQueryState } from '../utils/rison-helpers'
import type { ColumnGroup } from '../utils/column-groups'

export enum sortDirection {
    AZ = "az",
    ZA = "za",
    NEWEST = "newest",
    OLDEST = "oldest",
    NONE = "none",
}

// Type for custom sort configuration per column
// Maps sortDirection enum values to actual API parameter strings
export type ColumnSortConfig = {
    allowedDirections: Partial<Record<sortDirection, string>>
    default: sortDirection
}

// Map of column id to its sort configuration
export type ColumnSortConfigs = Record<string, ColumnSortConfig>

// State for a single table instance
export interface SingleTableState {
    // Table query state
    page: number
    pageSize: number
    sort: Array<{ id: string; parameter: sortDirection }>
    filters: Record<string, any>
    search: string
    columnSizes: Record<string, number>
    columnPinned: ColumnPinningState
    columnVisibility: Record<string, boolean>
    columnOrder: ColumnOrderState
    temporaryColumnOrder?: ColumnOrderState | undefined
    selectedRowIds: string[]
    // Per-id snapshot of the row data that was visible at selection time. Lets
    // consumers render a "view selected" dialog for rows that have since been
    // filtered, searched or paged off screen. Stored as `unknown` so the slice
    // stays generic — features cast at the read boundary via `useSelectedRowsData<T>`.
    selectedRowsData: Record<string, unknown>
    /**
     * Inverted-selection mode. When true, every row matching the current query is
     * selected EXCEPT those in `excludeIds`, and `selectedRowIds` is ignored.
     * Nothing in this package turns it on — a feature calls `selectAll()` from
     * `useTableSelection`. Tables that never do keep this false forever.
     */
    allSelected: boolean
    /** Rows deselected while `allSelected`. Meaningless when `allSelected` is false. */
    excludeIds: string[]
    /**
     * User-created column groups, in panel order.
     *
     * Made in the column-settings panel rather than declared in code, so the table's
     * column definitions know nothing about them.
     */
    columnGroups: ColumnGroup[]
    /**
     * Ids of column groups currently EXPANDED.
     *
     * Stored as the expanded set rather than the collapsed one so groups default to
     * collapsed with no state — a table that has never been touched shows each group
     * as its single summary column, which is the point of grouping.
     */
    expandedColumnGroups: string[]
    loading: boolean
    validating: boolean
    columnRePositioning: boolean
    total: number
    fetcherOpts: any
    uriQueryPrefix: string
    // Column sort configurations for custom API parameter mappings
    columnSortConfigs: ColumnSortConfigs
}

export interface TableState {
    tables: {
        [slug: string]: SingleTableState
    }
}

const createInitialTableState = (slug: string, initialState?: Partial<TableQueryState>): SingleTableState => ({
    // Table query state
    page: initialState?.page ?? 0,
    pageSize: initialState?.pageSize ?? 100, // Default to 100
    sort: initialState?.sort ?? [],
    filters: initialState?.filters ?? {},
    search: initialState?.search ?? '',
    columnSizes: initialState?.columns?.sizes ?? {},
    columnPinned: initialState?.columns?.pinned ?? { start: [], end: [] },
    columnVisibility: initialState?.columns?.visibility ?? {},
    columnOrder: [],
    temporaryColumnOrder: undefined,
    selectedRowIds: initialState?.selectedRowIds ?? [],
    selectedRowsData: {},
    allSelected: initialState?.allSelected ?? false,
    excludeIds: initialState?.excludeIds ?? [],
    columnGroups: [],
    expandedColumnGroups: [],
    loading: false,
    validating: false,
    columnRePositioning: false,
    total: 0,
    fetcherOpts: null,
    uriQueryPrefix: slug,
    columnSortConfigs: {},
})

const initialState: TableState = {
    tables: {},
}

const tableSlice = createSlice({
    name: 'table',
    initialState,
    reducers: {

        initializeTable: (
            state,
            action: PayloadAction<{ slug: string; initialState?: Partial<TableQueryState> }>
        ) => {
            const { slug, initialState: initial } = action.payload
            if (!state.tables[slug]) {
                state.tables[slug] = createInitialTableState(slug, initial)
            }
        },

        removeTable: (state, action: PayloadAction<string>) => {
            delete state.tables[action.payload]
        },

        setPage: (state, action: PayloadAction<{ slug: string; page: number }>) => {
            const { slug, page } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].page = page
            }
        },

        setPageSize: (state, action: PayloadAction<{ slug: string; pageSize: number }>) => {
            const { slug, pageSize } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].pageSize = pageSize
                state.tables[slug].page = 0 // Reset to first page
            }
        },

        setSort: (state, action: PayloadAction<{ slug: string; sort: Array<{ id: string; parameter: sortDirection }> }>) => {
            const { slug, sort } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].sort = sort
            }
        },

        setSingleColumnSort: (
            state,
            action: PayloadAction<{ slug: string; column: string; parameter: sortDirection }>
        ) => {
            const { slug, column, parameter } = action.payload
            if (state.tables[slug]) {
                const newSort = [{ id: column, parameter }]
                state.tables[slug].sort = newSort

            }
        },

        mutateColumnSort: (
            state,
            action: PayloadAction<{ slug: string; column: string; parameter: sortDirection }>
        ) => {
            const { slug, column, parameter } = action.payload
            if (state.tables[slug]) {
                const existingSort = state.tables[slug].sort.filter(s => s.id !== column)
                state.tables[slug].sort = [...existingSort, { id: column, parameter }]

            }
        },

        removeColumnSort: (state, action: PayloadAction<{ slug: string; column: string }>) => {
            const { slug, column } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].sort = state.tables[slug].sort.filter(s => s.id !== column)
            }
        },

        resetColumnSort: (state, action: PayloadAction<string>) => {
            const slug = action.payload
            if (state.tables[slug]) {
                state.tables[slug].sort = []
            }
        },

        setFilters: (state, action: PayloadAction<{ slug: string; filters: Record<string, any> }>) => {
            const { slug, filters } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].filters = filters
                state.tables[slug].page = 0 // Reset to first page
            }
        },

        updateFilter: (state, action: PayloadAction<{ slug: string; key: string; value: any }>) => {
            const { slug, key, value } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].filters[key] = value
                state.tables[slug].page = 0
            }
        },

        removeFilter: (state, action: PayloadAction<{ slug: string; key: string }>) => {
            const { slug, key } = action.payload
            if (state.tables[slug]) {
                delete state.tables[slug].filters[key]
                state.tables[slug].page = 0
            }
        },

        clearFilters: (state, action: PayloadAction<string>) => {
            const slug = action.payload
            if (state.tables[slug]) {
                state.tables[slug].filters = {}
                state.tables[slug].page = 0
            }
        },

        setSearch: (state, action: PayloadAction<{ slug: string; search: string }>) => {
            const { slug, search } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].search = search
                state.tables[slug].page = 0 // Reset to first page
            }
        },

        setColumnSizes: (state, action: PayloadAction<{ slug: string; sizes: Record<string, number> }>) => {
            const { slug, sizes } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnSizes = sizes
            }
        },

        setColumnPinned: (
            state,
            action: PayloadAction<{ slug: string; pinned: ColumnPinningState }>
        ) => {
            const { slug, pinned } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnPinned = pinned
            }
        },

        setColumnOrder: (state, action: PayloadAction<{ slug: string; order: ColumnOrderState }>) => {
            const { slug, order } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnOrder = order
            }
        },

        setTemporaryColumnOrder: (state, action: PayloadAction<{ slug: string; order: ColumnOrderState | undefined }>) => {
            const { slug, order } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].temporaryColumnOrder = order
            }
        },


        setColumnVisibility: (state, action: PayloadAction<{ slug: string; visibility: Record<string, boolean> }>) => {
            const { slug, visibility } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnVisibility = visibility
            }
        },

        setSelectedRowIds: (
            state,
            action: PayloadAction<{ slug: string; ids: string[]; data?: Record<string, unknown> }>
        ) => {
            const { slug, ids, data } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].selectedRowIds = ids
                // Merge newly-provided snapshots, then prune entries whose id is no
                // longer selected. Keeps the snapshot map in lockstep with
                // `selectedRowIds` so callers never manage two stores.
                const existing = state.tables[slug].selectedRowsData || {}
                const merged: Record<string, unknown> = { ...existing, ...(data || {}) }
                const next: Record<string, unknown> = {}
                for (const id of ids) {
                    if (merged[id] !== undefined) next[id] = merged[id]
                }
                state.tables[slug].selectedRowsData = next
            }
        },
        /**
         * Enter or leave inverted-selection mode.
         *
         * Turning it on clears both lists: the two modes are mutually exclusive, so
         * carrying a stale inclusion list into exclusion mode would make `count` and
         * `payload` disagree about what is selected.
         */
        setAllSelected: (state, action: PayloadAction<{ slug: string; allSelected: boolean }>) => {
            const { slug, allSelected } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].allSelected = allSelected
                state.tables[slug].selectedRowIds = []
                state.tables[slug].selectedRowsData = {}
                state.tables[slug].excludeIds = []
            }
        },

        setColumnGroups: (state, action: PayloadAction<{ slug: string; groups: ColumnGroup[] }>) => {
            const { slug, groups } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnGroups = groups
            }
        },

        /**
         * Create a group from the given columns.
         *
         * A column belongs to at most one group, so any it already sat in gives it up —
         * otherwise the tree builder would emit it twice.
         */
        createColumnGroup: (
            state,
            action: PayloadAction<{ slug: string; id: string; name: string; columnIds: string[]; parentId?: string }>
        ) => {
            const { slug, id, name, columnIds, parentId } = action.payload
            const table = state.tables[slug]
            if (!table) return

            const taken = new Set(columnIds)
            table.columnGroups = table.columnGroups.map(group => ({
                ...group,
                columnIds: group.columnIds.filter(columnId => !taken.has(columnId)),
            }))
            table.columnGroups.push({ id, name, columnIds, parentId })
        },

        /** Recolour a group. `undefined` clears it back to inheriting, or to no fill. */
        setColumnGroupColor: (
            state,
            action: PayloadAction<{ slug: string; groupId: string; color?: { header: string; cell: string } }>
        ) => {
            const { slug, groupId, color } = action.payload
            const group = state.tables[slug]?.columnGroups.find(g => g.id === groupId)
            if (group) group.color = color
        },

        /**
         * Move a group inside another, or out to the top level with `parentId: undefined`.
         *
         * A group cannot be its own ancestor: re-parenting onto a descendant would make
         * a cycle that `indexGroups` would then have to break at render, so it is
         * refused here instead.
         */
        setColumnGroupParent: (
            state,
            action: PayloadAction<{ slug: string; groupId: string; parentId?: string }>
        ) => {
            const { slug, groupId, parentId } = action.payload
            const table = state.tables[slug]
            if (!table || groupId === parentId) return

            const group = table.columnGroups.find(g => g.id === groupId)
            if (!group) return

            if (parentId) {
                const byId = new Map(table.columnGroups.map(g => [g.id, g]))
                const seen = new Set<string>()
                let ancestor = byId.get(parentId)
                while (ancestor && !seen.has(ancestor.id)) {
                    if (ancestor.id === groupId) return
                    seen.add(ancestor.id)
                    ancestor = ancestor.parentId ? byId.get(ancestor.parentId) : undefined
                }
            }

            group.parentId = parentId
        },

        renameColumnGroup: (state, action: PayloadAction<{ slug: string; groupId: string; name: string }>) => {
            const { slug, groupId, name } = action.payload
            const group = state.tables[slug]?.columnGroups.find(g => g.id === groupId)
            if (group) group.name = name
        },

        /** Remove a group. Its columns return to the ungrouped list, never deleted. */
        deleteColumnGroup: (state, action: PayloadAction<{ slug: string; groupId: string }>) => {
            const { slug, groupId } = action.payload
            const table = state.tables[slug]
            if (!table) return

            table.columnGroups = table.columnGroups
                .filter(g => g.id !== groupId)
                // Promote any nested children rather than orphaning them.
                .map(g => (g.parentId === groupId ? { ...g, parentId: undefined } : g))
            table.expandedColumnGroups = table.expandedColumnGroups.filter(id => id !== groupId)
        },

        setColumnGroupMembers: (
            state,
            action: PayloadAction<{ slug: string; groupId: string; columnIds: string[] }>
        ) => {
            const { slug, groupId, columnIds } = action.payload
            const table = state.tables[slug]
            if (!table) return

            const taken = new Set(columnIds)
            table.columnGroups = table.columnGroups.map(group =>
                group.id === groupId
                    ? { ...group, columnIds }
                    : { ...group, columnIds: group.columnIds.filter(id => !taken.has(id)) },
            )
        },

        setExpandedColumnGroups: (state, action: PayloadAction<{ slug: string; groupIds: string[] }>) => {
            const { slug, groupIds } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].expandedColumnGroups = groupIds
            }
        },

        toggleColumnGroup: (state, action: PayloadAction<{ slug: string; groupId: string }>) => {
            const { slug, groupId } = action.payload
            const table = state.tables[slug]
            if (!table) return
            table.expandedColumnGroups = table.expandedColumnGroups.includes(groupId)
                ? table.expandedColumnGroups.filter(id => id !== groupId)
                : [...table.expandedColumnGroups, groupId]
        },

        setExcludeIds: (state, action: PayloadAction<{ slug: string; ids: string[] }>) => {
            const { slug, ids } = action.payload
            const table = state.tables[slug]
            if (table) {
                table.excludeIds = ids
                // Snapshots track whichever list is live — see `addExcludeId`.
                const existing = table.selectedRowsData || {}
                const next: Record<string, unknown> = {}
                for (const id of ids) {
                    if (existing[id] !== undefined) next[id] = existing[id]
                }
                table.selectedRowsData = next
            }
        },

        /**
         * Exclude a row, optionally snapshotting it.
         *
         * `selectedRowsData` holds snapshots for whichever list is currently live:
         * `selectedRowIds` in inclusion mode, `excludeIds` in exclusion mode. They
         * are never both populated (`setAllSelected` clears both), so one map serves
         * both without collision — and `SelectedRowsDialog` can render excluded rows
         * with real content instead of bare ids.
         */
        addExcludeId: (
            state,
            action: PayloadAction<{ slug: string; id: string; data?: unknown }>
        ) => {
            const { slug, id, data } = action.payload
            const table = state.tables[slug]
            if (table && !table.excludeIds.includes(id)) {
                table.excludeIds.push(id)
                if (data !== undefined) {
                    table.selectedRowsData = { ...(table.selectedRowsData || {}), [id]: data }
                }
            }
        },

        removeExcludeId: (state, action: PayloadAction<{ slug: string; id: string }>) => {
            const { slug, id } = action.payload
            const table = state.tables[slug]
            if (table) {
                table.excludeIds = table.excludeIds.filter(excluded => excluded !== id)
                // The row is back in the selection, so its exclusion snapshot is dead.
                const next = { ...(table.selectedRowsData || {}) }
                delete next[id]
                table.selectedRowsData = next
            }
        },

        resetRowSelection: (state, action: PayloadAction<string>) => {
            const slug = action.payload
            if (state.tables[slug]) {
                state.tables[slug].selectedRowIds = []
                state.tables[slug].selectedRowsData = {}
                state.tables[slug].allSelected = false
                state.tables[slug].excludeIds = []
            }
        },

        setLoading: (state, action: PayloadAction<{ slug: string; loading: boolean }>) => {
            const { slug, loading } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].loading = loading
            }
        },

        setValidating: (state, action: PayloadAction<{ slug: string; validating: boolean }>) => {
            const { slug, validating } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].validating = validating
            }
        },

        setColumnRePositioning: (state, action: PayloadAction<{ slug: string; positioning: boolean }>) => {
            const { slug, positioning } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnRePositioning = positioning
            }
        },

        setTotal: (state, action: PayloadAction<{ slug: string; total: number }>) => {
            const { slug, total } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].total = total
            }
        },

        setFetcherOpts: (state, action: PayloadAction<{ slug: string; opts: any }>) => {
            const { slug, opts } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].fetcherOpts = opts
            }
        },

        setColumnSortConfigs: (state, action: PayloadAction<{ slug: string; configs: ColumnSortConfigs }>) => {
            const { slug, configs } = action.payload
            if (state.tables[slug]) {
                state.tables[slug].columnSortConfigs = configs
            }
        },

        updateTableState: (state, action: PayloadAction<{ slug: string; updates: Partial<TableQueryState> }>) => {
            const { slug, updates } = action.payload
            if (state.tables[slug]) {
                if (updates.page !== undefined) state.tables[slug].page = updates.page
                if (updates.pageSize !== undefined) state.tables[slug].pageSize = updates.pageSize
                if (updates.sort !== undefined) {
                    state.tables[slug].sort = updates.sort

                }
                if (updates.filters !== undefined) state.tables[slug].filters = updates.filters
                if (updates.search !== undefined) state.tables[slug].search = updates.search
                if (updates.columns?.sizes !== undefined) state.tables[slug].columnSizes = updates.columns.sizes
                if (updates.columns?.pinned !== undefined) {
                    state.tables[slug].columnPinned = updates.columns.pinned as ColumnPinningState
                }
                if (updates.columns?.visibility !== undefined) state.tables[slug].columnVisibility = updates.columns.visibility
                if (updates.selectedRowIds !== undefined) {
                    state.tables[slug].selectedRowIds = updates.selectedRowIds
                    // Prune snapshots to the new id set, so URL hydration cannot leak
                    // stale rows from a prior selection.
                    const existingSnapshots = state.tables[slug].selectedRowsData || {}
                    const nextSnapshots: Record<string, unknown> = {}
                    for (const id of updates.selectedRowIds) {
                        if (existingSnapshots[id] !== undefined) nextSnapshots[id] = existingSnapshots[id]
                    }
                    state.tables[slug].selectedRowsData = nextSnapshots
                }
            }
        },

        resetTableState: (state, action: PayloadAction<{ slug: string; initialState?: Partial<TableQueryState> }>) => {
            const { slug, initialState: initial } = action.payload
            if (state.tables[slug]) {
                state.tables[slug] = createInitialTableState(slug, initial)
            }
        },
    },
})

// Export actions
export const {
    initializeTable,
    removeTable,
    setPage,
    setPageSize,
    setSort,
    setSingleColumnSort,
    mutateColumnSort,
    removeColumnSort,
    resetColumnSort,
    setFilters,
    updateFilter,
    removeFilter,
    clearFilters,
    setSearch,
    setColumnSizes,
    setColumnPinned,
    setColumnOrder,
    setTemporaryColumnOrder,
    setColumnVisibility,
    setSelectedRowIds,
    setAllSelected,
    setExcludeIds,
    setColumnGroups,
    createColumnGroup,
    renameColumnGroup,
    setColumnGroupColor,
    setColumnGroupParent,
    deleteColumnGroup,
    setColumnGroupMembers,
    setExpandedColumnGroups,
    toggleColumnGroup,
    addExcludeId,
    removeExcludeId,
    resetRowSelection,
    setLoading,
    setValidating,
    setColumnRePositioning,
    setTotal,
    setFetcherOpts,
    setColumnSortConfigs,
    updateTableState,
    resetTableState,
} = tableSlice.actions

export const selectTableState = (state: { table: TableState }, slug: string) => state.table.tables[slug]
export const selectTableExists = (state: { table: TableState }, slug: string) => !!state.table.tables[slug]

export default tableSlice.reducer
